import sumBy from 'lodash/sumBy';
import CouponHandler from './CouponHandler';

const DEFAULT_VAT_RATE = 0.2;

const normalizeCartItemKey = (itemOrId) => {
  if (itemOrId === null || typeof itemOrId === 'undefined') {
    return '';
  }

  if (typeof itemOrId === 'object') {
    return String(itemOrId.variation_id ?? itemOrId.id ?? '').trim();
  }

  return String(itemOrId).trim();
};

const normalizeQuantity = (value) => {
  const parsedValue = Number.parseInt(String(value ?? 0), 10);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
};

const normalizePrice = (value) => {
  const parsedValue = Number.parseFloat(String(value ?? 0));
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const includeVat = (value, vatRate = DEFAULT_VAT_RATE) => normalizePrice(value) * (1 + vatRate);

const dedupeCartItems = (cart = []) => {
  const mergedItems = new Map();

  (Array.isArray(cart) ? cart : []).forEach((item) => {
    const key = normalizeCartItemKey(item);

    if (!key) {
      return;
    }

    const normalizedItem = {
      ...item,
      id: String(item?.id ?? ''),
      variation_id: key,
      quantity: normalizeQuantity(item?.quantity),
      price: normalizePrice(item?.price),
    };

    if (!mergedItems.has(key)) {
      mergedItems.set(key, normalizedItem);
      return;
    }

    const existingItem = mergedItems.get(key);
    mergedItems.set(key, {
      ...existingItem,
      ...normalizedItem,
      quantity: normalizeQuantity(existingItem.quantity) + normalizedItem.quantity,
    });
  });

  return Array.from(mergedItems.values()).filter((item) => item.quantity > 0);
};

class CartHandler {
  constructor(cart = [], customer = null, coupons = [], shipping = null) {
    this.cart = dedupeCartItems(cart); // Array of cart items
    this.customer = customer; // Logged-in customer data (null for guests)
    this.coupons = coupons; // Applied coupons
    this.shipping = shipping ? this.normalizeShippingMethod(shipping) : null; // Selected shipping method
    this.taxRate = DEFAULT_VAT_RATE; // Tax Rate
    this.grossTotal = 0
    this.netTotal = 0
    this.totalDiscounts = 0
    this.couponHandler = this.getCouponHandler();
  }

  getCouponHandler(){
    this.couponHandler = new CouponHandler(this.cart, this.coupons);
    return this.couponHandler
  }

  normalizeShippingMethod(method = null) {
    if (!method) return null;

    const normalizedCost = Number.parseFloat(method.cost ?? method.total ?? 0);

    return {
      ...method,
      cost: Number.isNaN(normalizedCost) ? 0 : normalizedCost,
    };
  }

  recalculateAll() {
    // Recalculate all coupons
    this.coupons = this.coupons.map(coupon => {
      try {
        const discount = this.couponHandler.calculateDiscount();
        return { ...coupon, discount };
      } catch (error) {
        // If coupon becomes invalid, remove it
        // console.warn(`Coupon ${coupon.code} removed: ${error.message}`);
        return null;
      }
    }).filter(Boolean); // Remove invalid coupons

    // Update totals
    this.grossTotal = this.getGrossTotal();
    this.netTotal = this.getNetTotal();
    this.totalDiscounts = this.getTotalDiscount();
  }

  /** Adds an item to the cart */
  addItem(product) {
    if (!product?.id || typeof product.price === 'undefined') {
      // console.error('Invalid product data:', product);
      return;
    }

    const normalizedProductKey = normalizeCartItemKey(product);
    const normalizedProductQuantity = normalizeQuantity(product?.quantity || 1) || 1;
    const existingItemIndex = this.cart.findIndex(
      item => normalizeCartItemKey(item) === normalizedProductKey
    );
    
    if (existingItemIndex != -1) {
      const updatedCart = [...this.cart];
      updatedCart[existingItemIndex] = {
        ...updatedCart[existingItemIndex],
        quantity: normalizeQuantity(updatedCart[existingItemIndex].quantity) + normalizedProductQuantity
      };
      this.cart = dedupeCartItems(updatedCart);
    } else {
      this.cart = dedupeCartItems([
        ...this.cart,
        {
          ...product,
          id: String(product.id),
          variation_id: normalizedProductKey,
          quantity: normalizedProductQuantity,
          price: normalizePrice(product.price),
        },
      ]);
    }

    this.recalculateAll();
  }

  /** Removes an item from the cart */
  removeItem(productId) {
    if (!productId) {
      // console.error('Invalid productId:', productId);
      return;
    }
    const normalizedProductKey = normalizeCartItemKey(productId);
    this.cart = this.cart.filter(item => normalizeCartItemKey(item) !== normalizedProductKey);

    this.recalculateAll();
  }

  /** Updates quantity of an item */
  updateItemQuantity(productId, quantity) {
    if (!productId || quantity < 0) {
      // console.error('Invalid update parameters:', { productId, quantity });
      return;
    }

    if (quantity === 0) {
      this.removeItem(productId);
      return;
    }

    const normalizedProductKey = normalizeCartItemKey(productId);
    this.cart = dedupeCartItems(
      this.cart.map(item =>
        normalizeCartItemKey(item) === normalizedProductKey ? { ...item, quantity } : item
      )
    );

    this.recalculateAll();
  }

  /** Clears the cart */
  clearCart() {
    this.cart = [];
    this.coupons = [];
    this.shipping = null;

    this.recalculateAll();
  }

  isInCart(product) {
    return this.cart.some(item => normalizeCartItemKey(item) === normalizeCartItemKey(product));
  }

  /** Calculates subtotal (before tax, shipping, and discounts) */
  getSubtotal() {
    return this.cart.reduce((total, item) => {
      const itemPrice = parseFloat(item.price);
      const itemQuantity = parseInt(item.quantity, 10);
      
      if (isNaN(itemPrice) || isNaN(itemQuantity)) {
        // console.error('Invalid price or quantity:', item);
        return total;
      }
      
      return total + itemPrice * itemQuantity;
    }, 0);
  }

  getTotalItems() {
    return this.cart.reduce((total, item) => {
      const itemQuantity = parseInt(item.quantity, 10);
      return isNaN(itemQuantity) ? total : total + itemQuantity;
    }, 0);
  }

  getItemQuantity(productId) {
    const normalizedProductKey = normalizeCartItemKey(productId);
    const item = this.cart.find(item => normalizeCartItemKey(item) === normalizedProductKey);
    return item ? item.quantity : 0;
  }

  /** Applies a WooCommerce coupon */
  applyCoupon(couponData) {
    try {

      const hasAppliedCouponCode = this.coupons.some(coupon => coupon.code === couponData.code);
      
      if (hasAppliedCouponCode) {
        throw new Error('This coupon has already been applied to your cart');
      }

      const couponHandler = new CouponHandler(this.cart, couponData);
      const discount = couponHandler.calculateDiscount();
      this.coupons.push({ ...couponData, discount });
      // console.log(this)
    } catch (error) {
      throw new Error(error.message);
    }
  }

  /** Removes a coupon */
  removeCoupon(code) {
    this.coupons = this.coupons.filter(coupon => coupon.code !== code);
    this.recalculateAll();
  }

  /** Calculates total discount from all applied coupons */
  getTotalDiscount() {
    return sumBy(this.coupons, 'discount');
  }

  /** Calculates tax amount based on subtotal before coupon discounts */
  getTaxAmount() {
    return this.getSubtotal() * this.taxRate;
  }

  /** Calculates shipping cost (adjust based on WooCommerce shipping API) */
  getShippingCost() {
    return this.shipping ? includeVat(this.shipping.cost, this.taxRate) : 0;
  }

  setShippingMethod(method) {
    this.shipping = this.normalizeShippingMethod(method);
    this.recalculateAll();
  }

  clearShippingMethod() {
    this.shipping = null;
    this.recalculateAll();
  }

  /** Calculates final total (subtotal - discounts + tax + shipping) */
  getGrossTotal() {
    return this.getSubtotal() + this.getTaxAmount() + this.getShippingCost();
  }
  
  /** Calculates final total (subtotal - discounts + tax + shipping) */
  getNetTotal() {
    let total = this.getSubtotal() - this.getTotalDiscount() + this.getTaxAmount() + this.getShippingCost();
    return total < 0 ? 0 : total;
  }

}

export default CartHandler;
export { DEFAULT_VAT_RATE, dedupeCartItems };
